import type { ValidationErrors } from 'final-form';
import { FormSpy } from 'react-final-form';

import { Alert } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import { useFormI18n } from 'src/app/shared/form/hooks';

type Props = Readonly<{ errors: ValidationErrors }>;

const InvalidFormErrorMessage: React.FC<Props> = ({ errors }) => {
	const { getErrorMessage } = useFormI18n();

	if (!errors) return null;

	return (
		/*
		 * Hiding the list of errors for sighted users as they can see those errors in the form already.
		 */
		<Alert variant="error" className="sr-only mb-4">
			<Translate value="corgi.components.form.invalid.alert" />
			<ul>
				{Object.entries(errors).map(([name, error]) => {
					if ('key' in error)
						return (
							// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
							<li key={name}>{getErrorMessage(error)}</li>
						);
					// If the previous condition is false then that means this error came from the question validators
					return (
						// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
						<li key={name}>{getErrorMessage(error.answer)}</li>
					);
				})}
			</ul>
		</Alert>
	);
};

export const InvalidFormError: React.FC = () => {
	return (
		<FormSpy
			subscription={{
				hasValidationErrors: true,
				errors: true,
				modifiedSinceLastSubmit: true,
				submitFailed: true,
			}}
		>
			{({ hasValidationErrors, errors, submitFailed, modifiedSinceLastSubmit }) =>
				/*
				 * this results in the following behavior:
				 * - message is not visible unless form has been submitted at least once
				 * - message is not visible unless there are validation errors in the form
				 * - message is hidden as soon as any field is modified
				 * - message will reappear once the form is re-submitted and there are still validation errors
				 */
				hasValidationErrors && submitFailed && !modifiedSinceLastSubmit ? (
					<InvalidFormErrorMessage errors={errors} />
				) : null
			}
		</FormSpy>
	);
};
